"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
const stages = [{
  title: "Consultation",
  description: "We begin with a deep dive into your current operations to identify high-impact automation opportunities.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_consultation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_consultation.png-wvc-sizes",
    alt: "Strategic consultation session with data visualization"
  }
}, {
  title: "Analysis",
  description: "Our experts perform a comprehensive workflow analysis to map out the logic of your future AI systems.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_analysis.png-wvc-sizes",
    alt: "Detailed workflow mapping and data analysis interface"
  }
}, {
  title: "Implementation",
  description: "We deploy custom AI solutions and dashboards, ensuring seamless integration with your existing stack.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_implementation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_implementation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_implementation.png-wvc-sizes",
    alt: "Modern dashboard interface showing live automation metrics"
  }
}];
export default function ThePathToEfficiency() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollable = rect.height - windowHeight;
      if (totalScrollable <= 0) return;
      let currentProgress = -rect.top / totalScrollable;
      currentProgress = Math.max(0, Math.min(1, currentProgress));
      setProgress(currentProgress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const activeIndex = Math.min(2, Math.floor(progress * 3));
  return <section data-section-id="262092" id="efficiency" ref={sectionRef} className="bg-background text-foreground h-[300vh] relative">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden py-12 px-4 md:px-8 lg:px-16">
        
        {}
        <div className="text-center mb-12 md:mb-20 z-10">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tighter">
            The Path to Efficiency
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg md:text-xl font-default">
            A systematic approach to transforming your business with intelligent automation.
          </p>
        </div>

        {}
        <div className="w-full max-w-4xl relative mb-16 md:mb-24 z-10 px-4 md:px-12">
          {}
          <div className="absolute top-1/2 left-4 right-4 md:left-12 md:right-12 h-[1px] bg-primary/20 -translate-y-1/2"></div>
          
          {}
          <div className="absolute top-1/2 left-4 md:left-12 h-[2px] bg-primary -translate-y-1/2 transition-all duration-75 ease-linear" style={{
          width: `calc(${progress * 100}% - 2rem)`
        }}></div>

          {}
          <div className="relative flex justify-between items-center w-full">
            {stages.map((stage, i) => {
            const isActive = i <= activeIndex;
            const isCurrent = i === activeIndex;
            return <div key={i} data-index={i} className="flex flex-col items-center relative">
                  {}
                  <div className={`w-3 h-3 md:w-4 md:h-4 rounded-md border-2 transition-all duration-500 z-10 ${isActive ? "bg-primary border-primary" : "bg-background border-border"} ${isCurrent ? "shadow-[0_0_20px_rgba(var(--primary),0.8)] scale-125" : "scale-100"}`}></div>
                  
                  {}
                  <span className={`absolute top-8 font-mono text-xs md:text-sm tracking-widest transition-colors duration-500 ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                    0{i + 1}
                  </span>
                </div>;
          })}
          </div>

          {}
          <div className="absolute top-1/2 w-5 h-5 md:w-6 md:h-6 bg-primary rounded-md -translate-y-1/2 -translate-x-1/2 z-20 transition-all duration-75 ease-linear" style={{
          left: `calc(1rem + ${progress * 100}% * 0.9)`
        }}>
            <div className="absolute inset-1 bg-background rounded-md opacity-40"></div>
          </div>
        </div>

        {}
        <div className="relative w-full max-w-6xl h-[450px] md:h-[500px] lg:h-[550px]">
          {stages.map((stage, i) => {
          const isCurrent = i === activeIndex;
          return <div key={i} data-index={i} className={`absolute inset-0 transition-all duration-700 ease-in-out flex flex-col md:flex-row items-stretch ${isCurrent ? "opacity-100 translate-y-0 pointer-events-auto z-10" : "opacity-0 translate-y-12 pointer-events-none z-0"}`}>
                {}
                <div className="w-full md:w-1/2 h-64 md:h-full relative overflow-hidden border border-primary/10 bg-card/20 backdrop-blur-md p-2 md:p-4 rounded-lg">
                  <div className="w-full h-full relative overflow-hidden border border-white/5 rounded-md">
                    <img src={stage.image.src} data-wvc-srcset={stage.image.srcset} data-wvc-sizes={stage.image.sizes} alt={stage.image.alt} className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent md:hidden"></div>
                  </div>
                </div>

                {}
                <div className="w-full md:w-1/2 flex flex-col justify-center p-8 md:p-12 lg:p-16 bg-card/5 backdrop-blur-xl border border-white/10 border-t-0 md:border-t md:border-l-0 h-full relative overflow-hidden rounded-lg">
                  {}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-lg blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
                  
                  <div className="relative z-10">
                    <span className="text-xs md:text-sm uppercase tracking-widest text-primary font-mono mb-4 block">
                      Phase 0{i + 1}
                    </span>
                    <h3 className="font-default text-3xl md:text-4xl lg:text-5xl font-semibold mb-6 text-foreground tracking-tight">
                      {stage.title}
                    </h3>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 font-default">
                      {stage.description}
                    </p>
                    
                    {}
                    {i === 2 && <div className="mt-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 fill-mode-both">
                        
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}